import * as React from 'react';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
} from 'echarts/components';
import { LabelLayout, UniversalTransition } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { format, parseISO } from 'date-fns';
import { Box } from '@mui/material';
import type {
    DatasetComponentOption,
    GridComponentOption,
    LineSeriesOption,
    TitleComponentOption,
    TooltipComponentOption,
} from 'echarts';

type ECOption = echarts.ComposeOption<
    | LineSeriesOption
    | TitleComponentOption
    | TooltipComponentOption
    | GridComponentOption
    | DatasetComponentOption
>;

echarts.use([
    TitleComponent,
    TooltipComponent,
    GridComponent,
    DatasetComponent,
    TransformComponent,
    LineChart,
    LabelLayout,
    UniversalTransition,
    SVGRenderer,
]);

export interface ActivityTrendPoint {
    period: string;
    count: number;
    period_start: string;
}

interface ActivityTrendChartProps {
    data?: ActivityTrendPoint[];
    period: 'daily' | 'weekly' | 'monthly' | 'quarterly';
    label: string;
    color?: string;
}

const ActivityTrendChart = ({
    data = [],
    period,
    label,
    color = '#1976d2',
}: ActivityTrendChartProps) => {
    const chartRef = React.useRef<HTMLDivElement>(null);
    const chartInstance = React.useRef<echarts.ECharts | null>(null);

    React.useEffect(() => {
        if (!chartRef.current) {
            return;
        }

        if (!chartInstance.current) {
            chartInstance.current = echarts.init(chartRef.current, undefined, { renderer: 'svg' });
        }

        const chartData = (data || [])
            .map((item) => {
                let date: Date;
                try {
                    if (item.period_start) {
                        date = parseISO(item.period_start);
                        if (Number.isNaN(date.getTime())) {
                            date = new Date(item.period_start);
                        }
                    } else {
                        date = new Date();
                    }
                } catch (_error) {
                    date = new Date(item.period_start || new Date());
                }
                return [date.getTime(), Number(item.count) || 0] as [number, number];
            })
            .filter((item) => !Number.isNaN(item[0]) && !Number.isNaN(item[1]));

        const formatAxisDate = (value: number) => {
            const date = new Date(value);
            if (period === 'daily' || period === 'weekly') {
                return format(date, 'MMM dd');
            }
            if (period === 'monthly') {
                return format(date, 'MMM yyyy');
            }
            return format(date, 'QQQ yyyy');
        };

        const formatTooltipDate = (value: number) => {
            const date = new Date(value);
            if (period === 'daily' || period === 'weekly') {
                return format(date, 'MMM dd, yyyy');
            }
            if (period === 'monthly') {
                return format(date, 'MMMM yyyy');
            }
            return format(date, 'QQQ yyyy');
        };

        const option: ECOption = {
            xAxis: {
                type: 'time',
                axisLabel: {
                    formatter: (value: number) => formatAxisDate(value),
                },
            },
            yAxis: {
                type: 'value',
                minInterval: 1,
                splitLine: {
                    show: true,
                    lineStyle: {
                        type: [3, 4],
                        color: '#aaa',
                    },
                },
            },
            tooltip: {
                trigger: 'axis',
                formatter: (params: any) => {
                    const param = Array.isArray(params) ? params[0] : params;
                    return `${formatTooltipDate(param.value[0])}: ${Number(param.value[1] || 0).toLocaleString()} ${label.toLowerCase()}`;
                },
                axisPointer: {
                    type: 'line',
                    lineStyle: {
                        type: [3, 3],
                    },
                },
            },
            grid: {
                left: '0%',
                right: '1%',
                bottom: '0%',
                top: '8%',
                containLabel: true,
            },
            series: [
                {
                    name: label,
                    type: 'line',
                    smooth: true,
                    smoothMonotone: 'x',
                    symbol: 'circle',
                    symbolSize: 6,
                    areaStyle: {
                        color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                            { offset: 0.05, color: `${color}CC` },
                            { offset: 0.95, color: `${color}00` },
                        ]),
                    },
                    lineStyle: {
                        color,
                        width: 2,
                    },
                    itemStyle: {
                        color,
                    },
                    data: chartData,
                },
            ],
        };

        chartInstance.current.setOption(option, true);

        const handleResize = () => chartInstance.current?.resize();
        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [data, period, label, color]);

    React.useEffect(() => {
        return () => {
            chartInstance.current?.dispose();
            chartInstance.current = null;
        };
    }, []);

    return <Box ref={chartRef} sx={{ width: '100%', height: 260 }} />;
};

export default ActivityTrendChart;
